import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  BottomNavigation,
  BottomNavigationAction,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  Select,
  MenuItem,
  Badge,
  Chip,
  Divider,
  Tooltip,
  CardActions,
  CardHeader,
  Fade,
  Skeleton
} from '@mui/material';
import {
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  ExitToApp as LogoutIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Language as LanguageIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Today as TodayIcon,
  PlayArrow as PlayArrowIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  FolderOpen as FolderOpenIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { changeAppToken } from '../../redux/slices/applicationTokenSlice';
import { changeLoginData } from '../../redux/slices/loginSlice';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import '../../styles/UserDashboard.css';

// API base URL ve headers'ı sabit olarak tanımla
const API_BASE_URL = 'https://app.hhoms.com/api';
// const API_HEADERS = {
//   'Content-Type': 'application/json',
//   'ApplicationToken': '7db7cfd1-6ea2-487d-8f0a-a830e31eff94'
// };

// Custom hook: API çağrıları için


// Custom hook: Kullanıcı bilgileri için
const useUserInfo = () => {
  const reduxUserData = useSelector((state) => {
    // State'in varlığını kontrol et
    if (!state || !state.loginData) {
      return null;
    }
    
    const reduxData = state.loginData.loginData;
    
    if (!reduxData || !reduxData.id) {
      // Önce form kullanıcısı verilerini kontrol et
      const formUserData = localStorage.getItem('formUserData');
      if (formUserData) {
        try {
          return JSON.parse(formUserData);
        } catch (error) {
          console.error('Error parsing localStorage formUserData:', error);
        }
      }
      
      // Eğer form kullanıcısı verisi yoksa, panel kullanıcısı verilerini kontrol et
      const localUserData = localStorage.getItem('userData');
      if (localUserData) {
        try {
          return JSON.parse(localUserData);
        } catch (error) {
          console.error('Error parsing localStorage userData:', error);
        }
      }
    }
    
    return reduxData;
  });

  const userInfo = useMemo(() => {
    const info = {
      id: reduxUserData?.id,
      name: reduxUserData?.userData?.name || reduxUserData?.username || 'Kullanıcı',
      email: reduxUserData?.userData?.email || reduxUserData?.username || '',
      categories: reduxUserData?.userData?.categories,
      isActive: reduxUserData?.userData?.isActive !== false,
      token: reduxUserData?.userData?.token || reduxUserData?.token,
      ownerToken: reduxUserData?.userData?.ownerToken // ownertoken bilgisini ekle
    };
    
    console.log('useUserInfo - userData:', reduxUserData);
    console.log('useUserInfo - parsed userInfo:', info);
    
    return info;
  }, [reduxUserData]);

  return { userData: reduxUserData, userInfo };
};

// Form kartı bileşeni - Yeni gelişmiş tasarım
const ModernFormCard = React.memo(({ form, onClick, isMobile }) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  
  // Form durum rengini belirle
  const getStatusColor = () => {
    if (form.isActive && form.records > 0) return '#4caf50'; // Yeşil - Aktif ve kayıtlı
    if (form.isActive) return '#2196f3'; // Mavi - Aktif
    return '#ff9800'; // Turuncu - Pasif
  };

  if (isMobile) {
    return (
      <Fade in timeout={300}>
        <Tooltip 
          title={`${form.title} - Formu görüntülemek için tıklayın`}
          arrow
          placement="top"
        >
          <Card 
            sx={{ 
              mb: 2,
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #fff 0%, #f8f9ff 100%)',
              border: '1px solid rgba(65, 88, 208, 0.1)',
              '&:hover': { 
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(65, 88, 208, 0.15)',
                borderColor: 'rgba(65, 88, 208, 0.3)'
              }
            }}
            onClick={() => onClick(form.formId)}
          >
          <CardHeader
            avatar={
              <Badge 
                badgeContent={form.records} 
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    minWidth: '16px',
                    height: '16px'
                  }
                }}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: getStatusColor(),
                    width: 36,
                    height: 36,
                    background: `linear-gradient(135deg, ${getStatusColor()} 0%, ${getStatusColor()}dd 100%)`
                  }}
                >
                  <DescriptionIcon fontSize="small" />
                </Avatar>
              </Badge>
            }
            title={
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #2c3e50 0%, #4158D0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textFillColor: 'transparent',
                lineHeight: 1.3
              }}>
                {form.title}
              </Typography>
            }
            subheader={
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Form ID: {form.formId}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    size="small" 
                    label={form.isActive ? 'Aktif' : 'Pasif'}
                    color={form.isActive ? 'success' : 'warning'}
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Box>
            }
            action={
              <IconButton>
                <PlayArrowIcon color="primary" />
              </IconButton>
            }
          />
          
          <CardContent sx={{ pt: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon fontSize="small" />
                {form.createDate ? new Date(form.createDate).toLocaleDateString('tr-TR') : 'Tarih yok'}
              </Typography>
              <Typography variant="body2" sx={{ 
                fontWeight: 600,
                color: getStatusColor()
              }}>
                {form.records} kayıt
              </Typography>
            </Box>
          </CardContent>
        </Card>
        </Tooltip>
      </Fade>
    );
  }

  return (
    <Grid item xs={12} sm={6}>
      <Fade in timeout={300}>
        <Tooltip 
          title={`${form.title} - Formu görüntülemek için tıklayın`}
          arrow
          placement="top"
        >
          <Card 
            sx={{ 
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #fff 0%, #f8f9ff 100%)',
              border: '1px solid rgba(65, 88, 208, 0.1)',
              '&:hover': { 
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 25px rgba(65, 88, 208, 0.15)',
                borderColor: 'rgba(65, 88, 208, 0.3)',
              },
              overflow: 'hidden'
            }}
            onClick={() => onClick(form.formId)}
          >
          {/* Decorative element */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 80,
              height: 80,
              background: `linear-gradient(135deg, ${getStatusColor()}20 0%, ${getStatusColor()}10 100%)`,
              borderRadius: '0 0 0 100%',
              zIndex: 0
            }}
          />

          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: getStatusColor(),
                  flexShrink: 0
                }}
              >
                <DescriptionIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#2c3e50',
                    lineHeight: 1.3,
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {form.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip 
                    label={`ID: ${form.formId}`}
                    size="small"
                    sx={{ 
                      fontSize: '0.7rem',
                      height: 20,
                      bgcolor: 'rgba(65, 88, 208, 0.1)',
                      color: '#4158D0'
                    }}
                  />
                  <Chip 
                    label={form.isActive ? 'Aktif' : 'Pasif'}
                    size="small"
                    color={form.isActive ? 'success' : 'warning'}
                    sx={{ 
                      fontSize: '0.7rem',
                      height: 20
                    }}
                  />
                  <Chip 
                    label={`${form.records} kayıt`}
                    size="small"
                    sx={{ 
                      fontSize: '0.7rem',
                      height: 20,
                      bgcolor: 'rgba(76, 175, 80, 0.1)',
                      color: '#4caf50'
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon fontSize="small" />
                  {form.createDate ? new Date(form.createDate).toLocaleDateString('tr-TR') : 'Tarih yok'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        </Tooltip>
      </Fade>
    </Grid>
  );
});

// Form response kartı bileşeni - Yeni gelişmiş tasarım
const ModernResponseCard = React.memo(({ response, onClick, isMobile }) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  
  // Form verilerinin varlığını kontrol et
  const hasFormContent = response.formContent && response.formContent !== '';
  const hasFormData = response.formData && response.formData !== '';
  
  // Yanıt durumunu belirle
  const getStatusInfo = () => {
    if (hasFormContent && hasFormData) {
      return { 
        color: '#4caf50', 
        text: 'Tamamlandı', 
        icon: <CheckCircleIcon fontSize="small" />,
        progress: 100
      };
    } else if (hasFormContent || hasFormData) {
      return { 
        color: '#ff9800', 
        text: 'Kısmi Veri', 
        icon: <ScheduleIcon fontSize="small" />,
        progress: 50
      };
    } else {
      return { 
        color: '#f44336', 
        text: 'Eksik Veri', 
        icon: <ScheduleIcon fontSize="small" />,
        progress: 0
      };
    }
  };

  const statusInfo = getStatusInfo();
  
  if (isMobile) {
    return (
      <Fade in timeout={300}>
        <Tooltip 
          title={`${response.formTitle} - Yanıtı görüntülemek için tıklayın`}
          arrow
          placement="top"
        >
          <Card 
            sx={{ 
              mb: 2,
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #fff 0%, #f0fff4 100%)',
              border: `1px solid ${statusInfo.color}20`,
              '&:hover': { 
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 30px ${statusInfo.color}20`,
                borderColor: `${statusInfo.color}40`
            }
          }}
          onClick={() => onClick(response.id)}
        >
          <CardHeader
            avatar={
              <Avatar 
                sx={{ 
                  bgcolor: statusInfo.color,
                  width: 36,
                  height: 36,
                  background: `linear-gradient(135deg, ${statusInfo.color} 0%, ${statusInfo.color}dd 100%)`
                }}
              >
                <AssessmentIcon fontSize="small" />
              </Avatar>
            }
            title={
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                fontSize: '1rem',
                background: `linear-gradient(135deg, #2c3e50 0%, ${statusInfo.color} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textFillColor: 'transparent',
                lineHeight: 1.3
              }}>
                {response.formTitle}
              </Typography>
            }
            subheader={
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {new Date(response.submittedAt).toLocaleDateString('tr-TR')} - {new Date(response.submittedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    size="small" 
                    icon={statusInfo.icon}
                    label={statusInfo.text}
                    sx={{ 
                      bgcolor: `${statusInfo.color}15`,
                      color: statusInfo.color,
                      fontWeight: 500
                    }}
                  />
                </Box>
              </Box>
            }
            action={
              <IconButton>
                <VisibilityIcon sx={{ color: statusInfo.color }} />
              </IconButton>
            }
          />
          
          <CardContent sx={{ pt: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Form ID: {response.formId}
              </Typography>
              {response.score && (
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  color: statusInfo.color
                }}>
                  Puan: {response.score}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
        </Tooltip>
      </Fade>
    );
  }

  return (
    <Grid item xs={12} sm={6}>
      <Fade in timeout={300}>
        <Tooltip 
          title={`${response.formTitle} - Yanıtı görüntülemek için tıklayın`}
          arrow
          placement="top"
        >
          <Card 
            sx={{ 
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #fff 0%, #f0fff4 100%)',
              border: `1px solid ${statusInfo.color}20`,
              '&:hover': { 
                transform: 'translateY(-6px)',
                boxShadow: `0 12px 40px ${statusInfo.color}20`,
                borderColor: `${statusInfo.color}40`,
                '& .card-header': {
                  background: `linear-gradient(135deg, ${statusInfo.color} 0%, ${statusInfo.color}dd 100%)`,
                  color: 'white'
                }
              },
              position: 'relative',
              overflow: 'hidden'
          }}
          onClick={() => onClick(response.id)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Decorative element */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 80,
              height: 80,
              background: `linear-gradient(135deg, ${statusInfo.color}20 0%, ${statusInfo.color}10 100%)`,
              borderRadius: '0 0 0 100%',
              zIndex: 0
            }}
          />

          <CardHeader
            className="card-header"
            sx={{
              pb: 1,
              transition: 'all 0.3s ease',
              position: 'relative',
              zIndex: 1
            }}
            avatar={
              <Avatar 
                sx={{ 
                  bgcolor: statusInfo.color,
                  width: 44,
                  height: 44,
                  background: `linear-gradient(135deg, ${statusInfo.color} 0%, ${statusInfo.color}dd 100%)`,
                  boxShadow: `0 4px 12px ${statusInfo.color}30`
                }}
              >
                <AssessmentIcon />
              </Avatar>
            }
            title={
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
                mb: 0.5,
                lineHeight: 1.3
              }}>
                {response.formTitle}
              </Typography>
            }
            subheader={
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {new Date(response.submittedAt).toLocaleDateString('tr-TR')} - {new Date(response.submittedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            }
            action={
              <Tooltip title="Yanıtı Görüntüle">
                <IconButton 
                  sx={{ 
                    color: 'inherit',
                    '&:hover': { 
                      transform: 'scale(1.1)',
                      bgcolor: 'rgba(255,255,255,0.2)'
                    }
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            }
          />
          
          <CardContent sx={{ pt: 0, pb: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip 
                  size="small" 
                  icon={statusInfo.icon}
                  label={statusInfo.text}
                  sx={{ 
                    bgcolor: `${statusInfo.color}15`,
                    color: statusInfo.color,
                    fontWeight: 500
                  }}
                />
              </Box>
            </Box>
          </CardContent>

          <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              Form ID: {response.formId}
            </Typography>
            {response.score && (
              <Typography variant="body2" sx={{ 
                fontWeight: 600,
                color: statusInfo.color,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                <AnalyticsIcon fontSize="small" />
                Puan: {response.score}
              </Typography>
            )}
          </CardActions>
        </Card>
        </Tooltip>
      </Fade>
    </Grid>
  );
});

// Dashboard istatistik kartları
const DashboardStats = React.memo(({ assignedForms, formResponses, isMobile }) => {
  const stats = [
    {
      title: 'Toplam Form',
      value: assignedForms.length,
      icon: <AssignmentIcon />,
      color: '#4158D0',
      bgColor: 'rgba(65, 88, 208, 0.1)',
      trend: '+12%'
    },
    {
      title: 'Tamamlanan',
      value: formResponses.length,
      icon: <CheckCircleIcon />,
      color: '#4caf50',
      bgColor: 'rgba(76, 175, 80, 0.1)',
      trend: '+8%'
    },
    {
      title: 'Aktif Form',
      value: assignedForms.filter(f => f.isActive).length,
      icon: <TrendingUpIcon />,
      color: '#ff9800',
      bgColor: 'rgba(255, 152, 0, 0.1)',
      trend: '+5%'
    },
    {
      title: 'Bu Ay',
      value: formResponses.filter(r => {
        const responseDate = new Date(r.submittedAt);
        const now = new Date();
        return responseDate.getMonth() === now.getMonth() && responseDate.getFullYear() === now.getFullYear();
      }).length,
      icon: <TodayIcon />,
      color: '#9c27b0',
      bgColor: 'rgba(156, 39, 176, 0.1)',
      trend: '+23%'
    }
  ];

  if (isMobile) {
    return (
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          {stats.map((stat, index) => (
            <Grid item xs={6} key={index}>
              <Card 
                sx={{ 
                  background: `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}05 100%)`,
                  border: `1px solid ${stat.color}20`,
                  borderRadius: 3,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'translateY(-2px)' }
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar sx={{ 
                      bgcolor: stat.color, 
                      width: 32, 
                      height: 32,
                      mr: 1
                    }}>
                      {React.cloneElement(stat.icon, { fontSize: 'small' })}
                    </Avatar>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {stat.title}
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: stat.color, mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 500 }}>
                    {stat.trend}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Grid container spacing={3}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                background: `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}05 100%)`,
                border: `1px solid ${stat.color}20`,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 30px ${stat.color}20`
                },
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Decorative background element */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 80,
                  height: 80,
                  background: `linear-gradient(135deg, ${stat.color}20 0%, ${stat.color}10 100%)`,
                  borderRadius: '50%',
                  zIndex: 0
                }}
              />
              
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: stat.color, 
                    width: 48, 
                    height: 48,
                    mr: 2,
                    boxShadow: `0 4px 12px ${stat.color}30`
                  }}>
                    {stat.icon}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color }}>
                      {stat.value}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                    {stat.trend} bu ay
                  </Typography>
                  <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});

// Gelişmiş welcome banner
const WelcomeBanner = React.memo(({ userInfo, isMobile }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Günaydın';
    if (hour < 18) return 'İyi günler';
    return 'İyi akşamlar';
  };

  if (isMobile) {
    return (
      <Paper 
        elevation={0}
        sx={{ 
          mb: 3,
          background: 'linear-gradient(135deg, #4158D0 0%, #C850C0 100%)',
          color: 'white',
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {getGreeting()}, {userInfo.name}!
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
            Form yönetim panelinize hoş geldiniz
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon fontSize="small" />
            {currentTime.toLocaleString('tr-TR')}
          </Typography>
        </Box>
        
        {/* Decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 100,
            height: 100,
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%',
            zIndex: 0
          }}
        />
      </Paper>
    );
  }

  return (
    <Paper 
      elevation={0}
      sx={{ 
        mb: 4,
        background: 'linear-gradient(135deg, #4158D0 0%, #C850C0 100%)',
        color: 'white',
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        minHeight: 120
      }}
    >
      <Grid container sx={{ height: '100%' }}>
        <Grid item xs={12}>
          <Box sx={{ p: 4, position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              {getGreeting()}, {userInfo.name}!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400, mb: 2 }}>
              Form yönetim panelinize hoş geldiniz
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon />
              {currentTime.toLocaleString('tr-TR')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      
      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 150,
          height: 150,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 120,
          height: 120,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '50%',
          zIndex: 0
        }}
      />
    </Paper>
  );
});
// Kategori klasörü bileşeni
const CategoryFolder = React.memo(({ category, forms, onFormClick, isMobile }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };
  
  if (isMobile) {
    return (
      <Box sx={{ mb: 3 }}>
        <Paper 
          elevation={2} 
          sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #4158D0 0%, #C850C0 100%)',
            cursor: 'pointer',
            '&:hover': { 
              boxShadow: 4,
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
          onClick={handleToggle}
        >
          <Box sx={{ 
            p: 2.5, 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderOpenIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Kategori {category}
              </Typography>
              <Chip 
                label={forms.length}
                size="small"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
            <IconButton size="small" sx={{ color: 'white' }}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          {isExpanded && (
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
              {forms.map((form) => (
                <ModernFormCard 
                  key={form.formId}
                  form={form}
                  onClick={onFormClick}
                  isMobile={isMobile}
                />
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Paper 
        elevation={2} 
        sx={{ 
          borderRadius: 3, 
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #4158D0 0%, #C850C0 100%)',
          cursor: 'pointer',
          '&:hover': { 
            boxShadow: 6,
            transform: 'translateY(-2px)'
          },
          transition: 'all 0.3s ease'
        }}
        onClick={handleToggle}
      >
        <Box sx={{ 
          p: 3, 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>
              <FolderOpenIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Kategori {category}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {forms.length} form bulunuyor
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={forms.length}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                fontSize: '1rem'
              }}
            />
            <IconButton size="large" sx={{ color: 'white' }}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>
        
        {isExpanded && (
          <Box sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.05)' }}>
            <Grid container spacing={2} sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' },
              gap: 2
            }}>
              {forms.map((form) => (
                <ModernFormCard 
                  key={form.formId}
                  form={form}
                  onClick={onFormClick}
                  isMobile={isMobile}
                />
              ))}
            </Grid>
          </Box>
        )}
      </Paper>
    </Box>
  );
});

// Yanıt kategori klasörü bileşeni
const ResponseCategoryFolder = React.memo(({ category, responses, onResponseClick, isMobile }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };
  
  if (isMobile) {
    return (
      <Box sx={{ mb: 3 }}>
        <Paper 
          elevation={2} 
          sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #4caf50 0%, #43a047 100%)',
            cursor: 'pointer',
            '&:hover': { 
              boxShadow: 4,
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
          onClick={handleToggle}
        >
          <Box sx={{ 
            p: 2.5, 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Kategori {category}
              </Typography>
              <Chip 
                label={responses.length}
                size="small"
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
            <IconButton size="small" sx={{ color: 'white' }}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          {isExpanded && (
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}>
              {responses.map((response) => (
                <ModernResponseCard 
                  key={response.id}
                  response={response}
                  onClick={onResponseClick}
                  isMobile={isMobile}
                />
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Paper 
        elevation={2} 
        sx={{ 
          borderRadius: 3, 
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #4caf50 0%, #43a047 100%)',
          cursor: 'pointer',
          '&:hover': { 
            boxShadow: 6,
            transform: 'translateY(-2px)'
          },
          transition: 'all 0.3s ease'
        }}
        onClick={handleToggle}
      >
        <Box sx={{ 
          p: 3, 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>
              <AssessmentIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Kategori {category} Yanıtları
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {responses.length} yanıt bulunuyor
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={responses.length}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
                fontSize: '1rem'
              }}
            />
            <IconButton size="large" sx={{ color: 'white' }}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>
        
        {isExpanded && (
          <Box sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.05)' }}>
            <Grid container spacing={2} sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' },
              gap: 2
            }}>
              {responses.map((response) => (
                <ModernResponseCard 
                  key={response.id}
                  response={response}
                  onClick={onResponseClick}
                  isMobile={isMobile}
                />
              ))}
            </Grid>
          </Box>
        )}
      </Paper>
    </Box>
  );
});

const UserDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();
  /* const apiCall = useApiCall(); */
  const { userData, userInfo } = useUserInfo();
  const appToken = useSelector((state) => state.appToken?.apptoken || '');
  
  // Debug: Token durumunu logla
  console.log('UserDashboard - Token Debug:', {
    appToken: appToken,
    userInfoToken: userInfo?.token,
    userInfoOwnerToken: userInfo?.ownerToken,
    reduxState: useSelector((state) => state)
  });
  
  // State'ler
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [language, setLanguage] = useState(i18n.language || 'tr');
  const [assignedForms, setAssignedForms] = useState([]);
  const [formResponses, setFormResponses] = useState([]);
  const [groupedForms, setGroupedForms] = useState({});
  const [groupedResponses, setGroupedResponses] = useState({});

  // --- TAŞINDI: handleLogout fonksiyonu ---
  const handleLogout = useCallback(() => {
    // Form kullanıcısı için özel localStorage anahtarlarını temizle
    localStorage.removeItem('formUserToken');
    localStorage.removeItem('formUserData');
    localStorage.removeItem('formUserType');
    
    // Panel kullanıcısı için localStorage anahtarlarını da temizle (güvenlik için)
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('userType');
    
    dispatch(changeAppToken(null));
    dispatch(changeLoginData({}));
    
    navigate('/user-login');
  }, [dispatch, navigate]);
  // --- SON ---

  // Kullanıcının kategorilerine göre formları getir
  const fetchAssignedForms = useCallback(async () => {
    console.log('fetchAssignedForms çağrıldı');
    console.log('userInfo:', userInfo);

    if (!userInfo.token) {
      console.error('Token bulunamadı');
      setError('Kullanıcı token\'ı bulunamadı. Lütfen tekrar giriş yapın.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Yeni: Token ile formları getir
      const response = await axios.get(`${API_BASE_URL}/form/Libraries/list/login/${userInfo.token}/0`, {
        headers: {
          'Content-Type': 'application/json',
          ApplicationToken: userInfo.ownerToken || userInfo.token || appToken
        },
        timeout: 10000,
      });

      const data = response.data;
      console.log('Token ile formlar API yanıtı:', data);

      if (data?.formLibraries && Array.isArray(data.formLibraries)) {
        const forms = data.formLibraries.map(form => ({
          id: form.id,
          title: form.title || 'İsimsiz Form',
          description: form.description || '',
          category: form.parentId || 'Genel',
          progress: 0,
          totalQuestions: 0,
          completedQuestions: 0,
          folder: `Kategori ${form.parentId || 'Genel'}`,
          isCompleted: false,
          formId: form.formId || form.id,
          parentId: form.parentId,
          isActive: form.isActive !== undefined ? form.isActive : true,
          createDate: form.createDate,
          updateDate: form.editDate,
          formContent: form.formContent || null,
          records: form.records || 0
        }));

        setAssignedForms(forms);

        // Formları kategorilere göre gruplandır
        const grouped = forms.reduce((acc, form) => {
          const category = form.category || 'Genel';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(form);
          return acc;
        }, {});

        setGroupedForms(grouped);
      } else {
        setAssignedForms([]);
        setGroupedForms({});
        setError('Size atanmış form bulunamadı.');
      }
    } catch (error) {
      console.error('Formlar yüklenirken hata:', error);

      if (error.response) {
        const status = error.response.status;
        if (status === 401) {
          setError('Token geçersiz. Lütfen tekrar giriş yapın.');
          setTimeout(() => {
            handleLogout();
          }, 2000);
        } else if (status === 404) {
          setError('Bu token ile atanan form bulunamadı.');
        } else {
          setError(`Formlar yüklenirken hata: ${error.response.data?.message || 'Bilinmeyen hata'}`);
        }
      } else if (error.request) {
        setError('Sunucuya bağlanılamıyor. Lütfen internet bağlantınızı kontrol edin.');
      } else {
        setError(`Bağlantı hatası: ${error.message}`);
      }
      setAssignedForms([]);
      setGroupedForms({});
    } finally {
      setLoading(false);
    }
  }, [userInfo.token, handleLogout]);

  // Form yanıtlarını getir
  const fetchFormResponses = useCallback(async () => {
    if (!userInfo.token) {
      console.error('Token bulunamadı - form yanıtları');
      return;
    }

    console.log('Form yanıtları getiriliyor...');
    console.log('userInfo:', userInfo);

    try {
      // Kullanıcının kategorilerinden yanıtlanmış formları getir
      if (userInfo.categories) {
        const userCategories = userInfo.categories.split(',').map(cat => cat.trim()).filter(cat => cat);
        console.log('Yanıtlar için kullanıcı kategorileri:', userCategories);
        
        const allResponses = [];
        
        // Önce token ile yanıtları getirmeyi dene
        try {
          console.log('Token ile yanıtlar getiriliyor...');
          
          const tokenResponse = await axios.get(`${API_BASE_URL}/form/LibraryRecords/list/user/${userInfo.token}/0`, {
            headers: {
              'Content-Type': 'application/json',
              ApplicationToken: userInfo.ownerToken || userInfo.token || appToken
            },
            timeout: 10000
          });
          
          const tokenData = tokenResponse.data;
          console.log('Token ile yanıtlar API yanıtı:', tokenData);
          
          if (tokenData?.formLibraryRecords && Array.isArray(tokenData.formLibraryRecords)) {
            // Her yanıt için formId ile formContent ve formData'yı getir
            const tokenResponses = await Promise.all(
              tokenData.formLibraryRecords.map(async (record) => {
                const formId = record.formId || record.id;
                let formContent = record.formContent;
                let formData = record.formData;
                
                // Eğer formContent veya formData eksikse, formId ile getir
                if (!formContent || !formData) {
                  try {
                    console.log(`Form ${formId} için detay bilgileri getiriliyor...`);
                    
                    const formDetailResponse = await axios.get(`${API_BASE_URL}/form/Libraries/${formId}`, {
                      headers: {
                        'Content-Type': "application/json",
                        ApplicationToken: userInfo.ownerToken || userInfo.token || appToken
                      },
                      timeout: 10000
                    });
                    
                    const formDetail = formDetailResponse.data;
                    console.log(`Form ${formId} detay bilgileri:`, formDetail);
                    
                    if (formDetail?.formContent) {
                      formContent = formDetail.formContent;
                    }
                    
                    if (formDetail?.formData) {
                      formData = formDetail.formData;
                    }
                  } catch (formDetailError) {
                    console.error(`Form ${formId} detay bilgileri getirilemedi:`, formDetailError);
                  }
                }

                // --- GÜNCELLEME: String ise parse et ---
                if (typeof formContent === 'string') {
                  try { formContent = JSON.parse(formContent); } catch (e) { /* hata yönetimi */ }
                }
                if (typeof formData === 'string') {
                  try { formData = JSON.parse(formData); } catch (e) { /* hata yönetimi */ }
                }
                // --- SON ---
                
                return {
                  id: record.id,
                  formTitle: record.title || 'İsimsiz Form',
                  submittedAt: record.createDate || new Date().toISOString(),
                  status: record.status || 'completed',
                  score: record.score || null,
                  folder: 'Genel Klasör',
                  formId: formId,
                  parentId: record.parentId,
                  formContent: formContent,
                  formData: formData,
                  category: record.parentId || 'Genel'
                };
              })
            );
            
            console.log(`Token ile ${tokenResponses.length} yanıt bulundu`);
            setFormResponses(tokenResponses);
            
            // Yanıtları kategorilere göre gruplandır
            const grouped = tokenResponses.reduce((acc, response) => {
              const category = response.category || 'Genel';
              if (!acc[category]) {
                acc[category] = [];
              }
              acc[category].push(response);
              return acc;
            }, {});
            
            console.log('Token ile gruplandırılmış yanıtlar:', grouped);
            setGroupedResponses(grouped);
            return; // Token ile yanıtlar bulundu, kategorilerden getirmeye gerek yok
          }
        } catch (tokenError) {
          console.log('Token ile yanıtlar getirilemedi, kategorilerden deneniyor...');
          console.log('Token error:', tokenError.response?.data);
        }
        
        // Kategorilerden yanıtları getir
        for (const categoryId of userCategories) {
          try {
            console.log(`Kategori ${categoryId} için yanıtlar getiriliyor...`);
            
            // Önce sayfalama ile deneyelim
            let allCategoryResponses = [];
            let page = 1;
            let hasMorePages = true;
            
            try {
              while (hasMorePages) {
                console.log(`Kategori ${categoryId}, yanıtlar sayfa ${page} getiriliyor...`);
                
                // Farklı endpoint'leri deneyelim
                let response;
                try {
                  // İlk endpoint'i dene
                  response = await axios.get(`${API_BASE_URL}/form/LibraryRecords/${categoryId}`, {
                    headers: {
                      'Content-Type': "application/json",
                      'ApplicationToken': userInfo.ownerToken || userInfo.token || appToken
                    },
                    timeout: 10000,
                  });
                } catch (endpointError) {
                  console.log(`İlk endpoint başarısız, ikinci endpoint deneniyor...`);
                  // İkinci endpoint'i dene
                  response = await axios.get(`${API_BASE_URL}/form/LibraryRecords/list/${categoryId}`, {
                    headers: {
                      'Content-Type': "application/json",
                      'ApplicationToken': userInfo.ownerToken || userInfo.token || appToken
                    },
                    timeout: 10000,
                  });
                }
                const data = response.data;
                console.log(`Kategori ${categoryId}, yanıtlar sayfa ${page} API yanıtı:`, data);
                
                if (data?.formLibraryRecords && Array.isArray(data.formLibraryRecords)) {
                  allCategoryResponses.push(...data.formLibraryRecords);
                  
                  if (data.formLibraryRecords.length < 50) {
                    hasMorePages = false;
                  } else {
                    page++;
                  }
                } else {
                  hasMorePages = false;
                }
              }
            } catch (paginationError) {
              console.log(`Yanıtlar için sayfalama desteklenmiyor, tek seferde getiriliyor...`);
              console.log('Pagination error:', paginationError);
              
              // Fallback için de farklı endpoint'leri dene
              let response;
              try {
                response = await axios.get(`${API_BASE_URL}/form/LibraryRecords/${categoryId}`, {
                  headers: {
                    'Content-Type': "application/json",
                    'ApplicationToken': userInfo.ownerToken || userInfo.token || appToken
                  },
                  timeout: 10000
                });
              } catch (fallbackError) {
                console.log(`Fallback için ikinci endpoint deneniyor...`);
                response = await axios.get(`${API_BASE_URL}/form/LibraryRecords/list/${categoryId}`, {
                  headers: {
                    'Content-Type': "application/json",
                    'ApplicationToken': userInfo.ownerToken || userInfo.token || appToken
                  },
                  timeout: 10000
                });
              }
              const data = response.data;
              console.log(`Kategori ${categoryId} yanıtlar API yanıtı:`, data);
              
              if (data?.formLibraryRecords && Array.isArray(data.formLibraryRecords)) {
                allCategoryResponses = data.formLibraryRecords;
              }
            }
            
            console.log(`Kategori ${categoryId} için toplam ${allCategoryResponses.length} yanıt bulundu`);
            
            // Her yanıt için formId ile formContent ve formData'yı getir
            const categoryResponses = await Promise.all(
              allCategoryResponses.map(async (record) => {
                const formId = record.formId || record.id;
                let formContent = record.formContent;
                let formData = record.formData;
                
                // Eğer formContent veya formData eksikse, formId ile getir
                if (!formContent || !formData) {
                  try {
                    console.log(`Form ${formId} için detay bilgileri getiriliyor...`);
                    
                    const formDetailResponse = await axios.get(`${API_BASE_URL}/form/Libraries/${formId}`, {
                      headers: {
                        'Content-Type': "application/json",
                        ApplicationToken: userInfo.ownerToken || userInfo.token || appToken
                      },
                      timeout: 10000
                    });
                    
                    const formDetail = formDetailResponse.data;
                    console.log(`Form ${formId} detay bilgileri:`, formDetail);
                    
                    if (formDetail?.formContent) {
                      formContent = formDetail.formContent;
                    }
                    
                    if (formDetail?.formData) {
                      formData = formDetail.formData;
                    }
                  } catch (formDetailError) {
                    console.error(`Form ${formId} detay bilgileri getirilemedi:`, formDetailError);
                  }
                }

                // --- GÜNCELLEME: String ise parse et ---
                if (typeof formContent === 'string') {
                  try { formContent = JSON.parse(formContent); } catch (e) { /* hata yönetimi */ }
                }
                if (typeof formData === 'string') {
                  try { formData = JSON.parse(formData); } catch (e) { /* hata yönetimi */ }
                }
                // --- SON ---
                
                return {
                  id: record.id,
                  formTitle: record.title || 'İsimsiz Form',
                  submittedAt: record.createDate || new Date().toISOString(),
                  status: record.status || 'completed',
                  score: record.score || null,
                  folder: `Kategori ${categoryId}`,
                  formId: formId,
                  parentId: categoryId,
                  formContent: formContent,
                  formData: formData,
                  category: categoryId
                };
              })
            );
            
            allResponses.push(...categoryResponses);
            console.log(`Kategori ${categoryId} için ${categoryResponses.length} yanıt işlendi`);
          } catch (categoryError) {
            console.error(`Kategori ${categoryId} için yanıtlar getirilirken hata:`, categoryError);
            console.error('Category error details:', categoryError.response?.data);
            continue;
          }
        }
        
        // Duplicate yanıtları temizle
        const uniqueResponses = allResponses.filter((response, index, self) => 
          index === self.findIndex(r => r.id === response.id)
        );
        
        console.log(`Toplam ${uniqueResponses.length} benzersiz yanıt bulundu:`, uniqueResponses);
        console.log('Yanıtların formId\'leri:', uniqueResponses.map(r => ({ id: r.id, formId: r.formId, title: r.formTitle })));
        setFormResponses(uniqueResponses);
        
        // Yanıtları localStorage'a kaydet
        localStorage.setItem('formResponses', JSON.stringify(uniqueResponses));
        console.log('Yanıtlar localStorage\'a kaydedildi');
        
        // Yanıtları kategorilere göre gruplandır
        const grouped = uniqueResponses.reduce((acc, response) => {
          const category = response.category || 'Genel';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(response);
          return acc;
        }, {});
        
        console.log('Gruplandırılmış yanıtlar:', grouped);
        setGroupedResponses(grouped);
      } else {
        console.error('Kullanıcı kategorileri bulunamadı - yanıtlar');
        console.log('userInfo.categories:', userInfo.categories);
        setFormResponses([]);
        setGroupedResponses({});
      }
    } catch (error) {
      console.error('Form yanıtları yüklenirken hata:', error);
      console.error('Error details:', error.response?.data);
      setFormResponses([]);
      setGroupedResponses({});
    }
  }, [userInfo.token, userInfo.categories]);

  // Sayfa yüklendiğinde verileri getir
  useEffect(() => {
    if (userInfo.token) {
      fetchAssignedForms();
      fetchFormResponses();
    }
  }, [userInfo.token, fetchAssignedForms, fetchFormResponses]);

  const handleFormClick = useCallback((formId) => {
    const selectedForm = assignedForms.find(form => form.formId === formId);
    
    if (selectedForm) {
      localStorage.setItem('currentFormId', formId);
      localStorage.setItem('currentFormTitle', selectedForm.title);
      localStorage.setItem('currentFormContent', selectedForm.formContent || '');
      
      navigate(`/user-form/${formId}`);
    } else {
      setError('Form bulunamadı. Lütfen tekrar deneyin.');
    }
  }, [navigate, assignedForms]);

  const handleResponseClick = useCallback((responseId) => {
    console.log('Yanıt tıklandı, responseId:', responseId);
    
    // Yanıt detaylarını bul
    const selectedResponse = formResponses.find(response => response.id === responseId);
    
    if (selectedResponse) {
      // Yanıt detaylarını localStorage'a kaydet
      localStorage.setItem('currentResponseId', responseId);
      localStorage.setItem('currentResponseData', JSON.stringify(selectedResponse));
      localStorage.setItem('currentFormContent', selectedResponse.formContent || '');
      localStorage.setItem('currentFormData', selectedResponse.formData || '');
      localStorage.setItem('currentFormTitle', selectedResponse.formTitle || '');
      
      console.log('Yanıt detayları localStorage\'a kaydedildi:', selectedResponse);
      
      // Yanıt detay sayfasına yönlendir - responseId kullan
      navigate(`/form-response/${responseId}`);
    } else {
      console.error('Yanıt bulunamadı:', responseId);
      setError('Yanıt bulunamadı. Lütfen tekrar deneyin.');
    }
  }, [navigate, formResponses]);

  const clearError = useCallback(() => {
    setError('');
  }, []);

  const handleLanguageChange = useCallback((event) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
  }, [i18n]);

  // Kullanıcı kontrolü - hem userData hem de userInfo'yu kontrol et
  if (!userData || !userData.id || !userInfo.id) {
    setTimeout(() => {
      navigate('/user-login');
    }, 1000);
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <CircularProgress size={60} sx={{ color: 'white' }} />
        <Typography variant="body1" sx={{ color: 'white' }}>
          Kullanıcı bilgileri kontrol ediliyor...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      pb: isMobile ? 7 : 0,
      px: { xs: 1, sm: 2, md: 3 },
      py: 2
    }}>
      {/* Mobil AppBar */}
      {isMobile && (
        <AppBar position="fixed" sx={{ 
          background: 'linear-gradient(135deg, #4158D0 0%, #C850C0 100%)', 
          color: 'white',
          backdropFilter: 'blur(10px)'
        }}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 32, height: 32 }}>
                <PersonIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1 }}>
                  {userInfo.name}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1 }}>
                  Form Kullanıcısı
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={language}
                  onChange={handleLanguageChange}
                  sx={{ 
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '& .MuiSelect-icon': { color: 'white' },
                    '& .MuiSelect-select': { 
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }
                  }}
                >
                  <MenuItem value="tr">TR</MenuItem>
                  <MenuItem value="en">EN</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Çıkış Yap">
                <IconButton onClick={handleLogout} sx={{ color: 'white' }}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <Box sx={{ pt: isMobile ? 8 : 2, pb: 2, px: { xs: 1, sm: 2, md: 3 }, width: '100%' }}>
        {/* Desktop Welcome Banner */}
        {!isMobile && (
          <WelcomeBanner userInfo={userInfo} isMobile={isMobile} />
        )}

        {/* Mobile Welcome Banner */}
        {isMobile && (
          <WelcomeBanner userInfo={userInfo} isMobile={isMobile} />
        )}

        {/* Desktop Header Controls */}
        {!isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small">
                <Select
                  value={language}
                  onChange={handleLanguageChange}
                  sx={{ 
                    '& .MuiSelect-select': { 
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }
                  }}
                >
                  <MenuItem value="tr">
                    <LanguageIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    TR
                  </MenuItem>
                  <MenuItem value="en">
                    <LanguageIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    EN
                  </MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  fetchAssignedForms();
                  fetchFormResponses();
                }}
                disabled={loading}
                sx={{
                  borderColor: '#4158D0',
                  color: '#4158D0',
                  '&:hover': {
                    borderColor: '#C850C0',
                    color: '#C850C0',
                    background: 'rgba(65, 88, 208, 0.05)',
                  }
                }}
              >
                Yenile
              </Button>
            </Box>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                borderColor: '#f44336',
                color: '#f44336',
                '&:hover': {
                  borderColor: '#d32f2f',
                  color: '#d32f2f',
                  background: 'rgba(244, 67, 54, 0.05)',
                }
              }}
            >
              {t('logout')}
            </Button>
          </Box>
        )}

        {error && (
          <Fade in>
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-message': { width: '100%' }
              }} 
              onClose={clearError}
              action={
                <Button size="small" onClick={clearError}>
                  Kapat
                </Button>
              }
            >
              {error}
            </Alert>
          </Fade>
        )}

        {/* Loading durumu */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Veriler yükleniyor...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Ana İçerik */}
        {!loading && (
          <Paper 
            elevation={2} 
            sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              background: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)'
            }}
          >
            {/* Desktop Tab Navigation */}
            {!isMobile && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex' }}>
                  <Button
                    variant={activeTab === 0 ? "contained" : "text"}
                    onClick={() => setActiveTab(0)}
                    sx={{ 
                      flex: 1, 
                      borderRadius: 0, 
                      py: 2.5,
                      background: activeTab === 0 ? 'linear-gradient(135deg, #4158D0 0%, #C850C0 100%)' : 'transparent',
                      '&:hover': {
                        background: activeTab === 0 ? 'linear-gradient(135deg, #C850C0 0%, #4158D0 100%)' : 'rgba(65, 88, 208, 0.05)'
                      }
                    }}
                    startIcon={<AssignmentIcon />}
                  >
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('my_forms')}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {assignedForms.length} form bulunuyor
                      </Typography>
                    </Box>
                  </Button>
                  <Button
                    variant={activeTab === 1 ? "contained" : "text"}
                    onClick={() => setActiveTab(1)}
                    sx={{ 
                      flex: 1, 
                      borderRadius: 0, 
                      py: 2.5,
                      background: activeTab === 1 ? 'linear-gradient(135deg, #4caf50 0%, #43a047 100%)' : 'transparent',
                      '&:hover': {
                        background: activeTab === 1 ? 'linear-gradient(135deg, #43a047 0%, #4caf50 100%)' : 'rgba(76, 175, 80, 0.05)'
                      }
                    }}
                    startIcon={<AssessmentIcon />}
                  >
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('responses')}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {formResponses.length} yanıt bulunuyor
                      </Typography>
                    </Box>
                  </Button>
                </Box>
              </Box>
            )}

            <Box sx={{ p: isMobile ? 2 : 4 }}>
              {activeTab === 0 && (
                <Fade in timeout={300}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h4" sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #4158D0 0%, #C850C0 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textFillColor: 'transparent'
                      }}>
                        Atanan Formlarım
                      </Typography>
                      <Chip 
                        label={`${assignedForms.length} Form`}
                        sx={{ 
                          bgcolor: 'rgba(65, 88, 208, 0.1)',
                          color: '#4158D0',
                          fontWeight: 600,
                          fontSize: '0.9rem'
                        }}
                      />
                    </Box>
                    
                    {assignedForms.length === 0 ? (
                      <Paper sx={{ 
                        textAlign: 'center', 
                        py: 8,
                        background: 'linear-gradient(135deg, rgba(65, 88, 208, 0.05) 0%, rgba(200, 80, 192, 0.05) 100%)',
                        borderRadius: 3
                      }}>
                        <AssignmentIcon sx={{ fontSize: 80, color: '#4158D0', opacity: 0.3, mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 1, color: '#4158D0', fontWeight: 600 }}>
                          Henüz Form Atanmamış
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          Size atanan form bulunmuyor. Yöneticinizle iletişime geçin.
                        </Typography>
                      </Paper>
                    ) : (
                      <>
                        {isMobile ? (
                          <Box>
                            {assignedForms.map((form) => (
                              <ModernFormCard 
                                key={form.id}
                                form={form}
                                onClick={handleFormClick}
                                isMobile={isMobile}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Grid container spacing={{ xs: 2, sm: 2, md: 3, lg: 3, xl: 4 }}>
                            {assignedForms.map((form) => (
                              <ModernFormCard 
                                key={form.id}
                                form={form}
                                onClick={handleFormClick}
                                isMobile={isMobile}
                              />
                            ))}
                          </Grid>
                        )}
                      </>
                    )}
                  </Box>
                </Fade>
              )}

              {activeTab === 1 && (
                <Fade in timeout={300}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h4" sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #4caf50 0%, #43a047 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textFillColor: 'transparent'
                      }}>
                        Form Yanıtlarım
                      </Typography>
                      <Chip 
                        label={`${formResponses.length} Yanıt`}
                        sx={{ 
                          bgcolor: 'rgba(76, 175, 80, 0.1)',
                          color: '#4caf50',
                          fontWeight: 600,
                          fontSize: '0.9rem'
                        }}
                      />
                    </Box>
                    
                    {formResponses.length === 0 ? (
                      <Paper sx={{ 
                        textAlign: 'center', 
                        py: 8,
                        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(67, 160, 71, 0.05) 100%)',
                        borderRadius: 3
                      }}>
                        <AssessmentIcon sx={{ fontSize: 80, color: '#4caf50', opacity: 0.3, mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 1, color: '#4caf50', fontWeight: 600 }}>
                          Henüz Yanıt Yok
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          Henüz form yanıtınız bulunmuyor. Formlara yanıt vererek bu bölümü doldurun.
                        </Typography>
                      </Paper>
                    ) : (
                      <>
                        {isMobile ? (
                          <Box>
                            {formResponses.map((response) => (
                              <ModernResponseCard 
                                key={response.id}
                                response={response}
                                onClick={handleResponseClick}
                                isMobile={isMobile}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Grid container spacing={{ xs: 2, sm: 2, md: 3, lg: 3, xl: 4 }}>
                            {formResponses.map((response) => (
                              <ModernResponseCard 
                                key={response.id}
                                response={response}
                                onClick={handleResponseClick}
                                isMobile={isMobile}
                              />
                            ))}
                          </Grid>
                        )}
                      </>
                    )}
                  </Box>
                </Fade>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      {/* Mobil Bottom Navigation */}
      {isMobile && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1000,
            borderTop: '1px solid rgba(0,0,0,0.1)',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)'
          }} 
          elevation={8}
        >
          <BottomNavigation
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            showLabels
            sx={{
              background: 'transparent',
              '& .MuiBottomNavigationAction-root': {
                transition: 'all 0.3s ease',
                '&.Mui-selected': {
                  color: '#4158D0',
                  transform: 'scale(1.1)'
                }
              }
            }}
          >
            <BottomNavigationAction 
              label={`Formlar (${assignedForms.length})`}
              icon={
                <Badge badgeContent={assignedForms.length} color="primary">
                  <AssignmentIcon />
                </Badge>
              }
            />
            <BottomNavigationAction 
              label={`Yanıtlar (${formResponses.length})`}
              icon={
                <Badge badgeContent={formResponses.length} color="success">
                  <AssessmentIcon />
                </Badge>
              }
            />
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default UserDashboard;